import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { SellerContractService } from "@/services/sellerContract/sellerContractService";

function isFailureReturn(sp: URLSearchParams) {
  if (sp.has("error") || sp.has("error_description")) return true;
  const raw = sp.get("contractSignatureStatus");
  if (!raw) return false;
  const v = raw.toLowerCase();
  return (
    v === "declined" ||
    v === "error" ||
    v === "failed" ||
    v === "cancelled" ||
    v === "canceled"
  );
}

/**
 * Handles the BankID (Idura) return on /my-auctions for seller contracts,
 * mirroring OrdersView for buyer contracts: finalize (webhook fallback), show
 * result dialog, clean the URL and poll my-listings until the status updates.
 */
export const useSellerContractSignatureReturn = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [returnDetected, setReturnDetected] = useState(false);
  const finalizeStarted = useRef(false);

  const sp = new URLSearchParams(location.search);
  const hasReturnParams =
    sp.has("signatory_id") ||
    sp.has("signatory-token") ||
    sp.has("contractSignatureStatus") ||
    sp.has("contractSigned");

  useEffect(() => {
    if (!hasReturnParams) {
      finalizeStarted.current = false;
      return;
    }

    setReturnDetected(true);

    const params = new URLSearchParams(location.search);
    const failed = isFailureReturn(params);
    const alreadySigned = params.get("contractSigned") === "1";
    const signatoryId = params.get("signatory_id") || params.get("signatoryId");

    if (failed) {
      setShowFailure(true);
      setShowSuccess(false);
    } else if (alreadySigned) {
      setShowSuccess(true);
      setShowFailure(false);
    } else if (!finalizeStarted.current) {
      finalizeStarted.current = true;
      void (async () => {
        try {
          await SellerContractService.finalizePendingBankIdSignature({
            signatoryId,
          });
        } catch {
          /* webhook/polling may still succeed */
        }
        setShowSuccess(true);
        setShowFailure(false);
      })();
    }

    const dialogTimer = window.setTimeout(() => {
      setShowSuccess(false);
      setShowFailure(false);
    }, 3000);

    navigate(location.pathname, { replace: true });

    return () => window.clearTimeout(dialogTimer);
  }, [hasReturnParams, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!returnDetected) return;

    let attempts = 0;
    const maxAttempts = 8;
    let stopped = false;

    const refresh = async () => {
      attempts += 1;
      if (attempts <= 3 && user) {
        try {
          await SellerContractService.finalizePendingBankIdSignature();
        } catch {
          /* ignore */
        }
      }
      await queryClient.invalidateQueries({
        queryKey: ["listings", "my-listings"],
        refetchType: "active",
      });
    };

    void refresh();

    const intervalId = window.setInterval(() => {
      if (stopped) return;
      if (attempts >= maxAttempts) {
        window.clearInterval(intervalId);
        stopped = true;
        setReturnDetected(false);
        return;
      }
      void refresh();
    }, 1500);

    return () => {
      window.clearInterval(intervalId);
      stopped = true;
    };
  }, [returnDetected, queryClient, user]);

  return {
    showSuccess,
    showFailure,
    closeSuccess: () => setShowSuccess(false),
    closeFailure: () => setShowFailure(false),
  };
};
